import { isIosLike } from '../app/platform';
import type { DebugSettings, OrientationProvider, OrientationSample, Store } from '../contracts';
import { CircularLowPass, type CircularLowPassOptions } from '../pose/circular';
import {
  DEG2RAD,
  type HeadingPitch,
  type Quat,
  earthHeadingPitch,
  quatFromAxisAngle,
  quatFromDeviceOrientation,
  quatFromHeadingPitchRoll,
  quatMul,
  quatSet,
  wrap180,
  wrap360,
} from '../pose/math';
import { debugStore } from '../settings';
import { Listeners } from './listeners';

export type OrientationErrorCode = 'unsupported' | 'denied' | 'unreadable' | 'timeout';

export class OrientationError extends Error {
  readonly code: OrientationErrorCode;
  constructor(code: OrientationErrorCode, message: string) {
    super(message);
    this.name = 'OrientationError';
    this.code = code;
  }
}

/** Map a Generic Sensor error (DOMException name) to an OrientationError. */
export function mapSensorError(err: unknown): OrientationError {
  if (err instanceof OrientationError) return err;
  const name = (err as { name?: string } | null)?.name ?? '';
  const msg = (err as { message?: string } | null)?.message ?? String(err);
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return new OrientationError('denied', msg || 'Sensorzugriff verweigert');
    case 'NotReadableError':
      return new OrientationError('unreadable', msg || 'Sensor nicht lesbar');
    case 'NotSupportedError':
    case 'ReferenceError':
      return new OrientationError('unsupported', msg || 'Sensor nicht unterstützt');
    default:
      return new OrientationError('unreadable', msg || 'Sensorfehler');
  }
}

/**
 * Common part: one reused OrientationSample per provider (copy the fields if
 * you need to keep them), listener lists, heading/pitch computation.
 */
abstract class BaseOrientationProvider implements OrientationProvider {
  abstract readonly available: boolean;
  abstract start(): Promise<void>;
  abstract stop(): void;

  protected readonly sample: OrientationSample;
  protected hasSample = false;
  protected readonly samples = new Listeners<OrientationSample>();
  protected readonly errors = new Listeners<OrientationError>();
  private readonly hp: HeadingPitch = { headingDeg: 0, pitchDeg: 0 };

  constructor(source: OrientationSample['source']) {
    this.sample = { quaternion: [0, 0, 0, 1], headingMagDeg: 0, pitchDeg: 0, time: 0, source };
  }

  latest(): OrientationSample | null {
    return this.hasSample ? this.sample : null;
  }

  onSample(cb: (s: OrientationSample) => void): () => void {
    return this.samples.add(cb);
  }

  /** Runtime errors after start() (e.g. the sensor became unreadable). */
  onError(cb: (e: OrientationError) => void): () => void {
    return this.errors.add(cb);
  }

  protected publish(x: number, y: number, z: number, w: number, time: number): void {
    const s = this.sample;
    quatSet(s.quaternion, x, y, z, w);
    earthHeadingPitch(s.quaternion, 0, this.hp);
    s.headingMagDeg = this.hp.headingDeg;
    s.pitchDeg = this.hp.pitchDeg;
    s.time = time;
    this.hasSample = true;
    this.samples.emit(s);
  }
}

// ─── Generic Sensor API ──────────────────────────────────────────────────────

/** Minimal typing of the Generic Sensor API (not in lib.dom). */
export interface OrientationSensorLike extends EventTarget {
  readonly quaternion?: ArrayLike<number> | null;
  readonly timestamp?: number | null;
  start(): void;
  stop(): void;
}
export type OrientationSensorCtor = new (opts: {
  frequency: number;
  referenceFrame: 'device' | 'screen';
}) => OrientationSensorLike;

export interface AbsoluteOrientationOptions {
  /** Defaults to globalThis.AbsoluteOrientationSensor. */
  SensorCtor?: OrientationSensorCtor;
  /** Defaults to navigator.permissions (null disables the check). */
  permissions?: Pick<Permissions, 'query'> | null;
  frequency?: number;
  /** Reject start() if no reading arrives within this time (ms). */
  startTimeoutMs?: number;
  now?: () => number;
}

const SENSOR_PERMISSIONS = ['accelerometer', 'gyroscope', 'magnetometer'] as const;

/**
 * AbsoluteOrientationSensor (Android TYPE_ROTATION_VECTOR, magnetic north),
 * 60 Hz, referenceFrame 'device'. start() resolves on the first reading.
 */
export class AbsoluteOrientationProvider extends BaseOrientationProvider {
  private readonly Ctor: OrientationSensorCtor | undefined;
  private readonly permissions: Pick<Permissions, 'query'> | null;
  private readonly frequency: number;
  private readonly startTimeoutMs: number;
  private readonly now: () => number;
  private sensor: OrientationSensorLike | null = null;
  private failed = false;
  private startPromise: Promise<void> | null = null;
  private cleanup: (() => void) | null = null;

  constructor(opts: AbsoluteOrientationOptions = {}) {
    super('absolute-sensor');
    this.Ctor =
      opts.SensorCtor ??
      (globalThis as { AbsoluteOrientationSensor?: OrientationSensorCtor }).AbsoluteOrientationSensor;
    this.permissions =
      opts.permissions !== undefined
        ? opts.permissions
        : typeof navigator !== 'undefined' && navigator.permissions
          ? navigator.permissions
          : null;
    this.frequency = opts.frequency ?? 60;
    this.startTimeoutMs = opts.startTimeoutMs ?? 2000;
    this.now = opts.now ?? (() => performance.now());
  }

  get available(): boolean {
    return typeof this.Ctor === 'function' && !this.failed;
  }

  start(): Promise<void> {
    if (!this.startPromise) this.startPromise = this.doStart();
    return this.startPromise;
  }

  stop(): void {
    this.cleanup?.();
    this.cleanup = null;
    this.startPromise = null;
  }

  private fail(err: OrientationError): OrientationError {
    this.failed = true;
    this.stop();
    return err;
  }

  private async doStart(): Promise<void> {
    const Ctor = this.Ctor;
    if (typeof Ctor !== 'function') {
      throw this.fail(new OrientationError('unsupported', 'AbsoluteOrientationSensor nicht verfügbar'));
    }
    await this.checkPermissions();
    let sensor: OrientationSensorLike;
    try {
      sensor = new Ctor({ frequency: this.frequency, referenceFrame: 'device' });
    } catch (e) {
      throw this.fail(mapSensorError(e));
    }
    this.sensor = sensor;

    await new Promise<void>((resolve, reject) => {
      let started = false;
      const onReading = (): void => {
        const q = sensor.quaternion;
        if (!q || q.length < 4) return;
        // Stamp with performance.now() at reception, not sensor.timestamp: on Android the
        // sensor timestamp can use a different time origin than performance.now()/XR frame
        // time, which would make the engine treat fresh samples as stale.
        this.publish(q[0], q[1], q[2], q[3], this.now());
        if (!started) {
          started = true;
          clearTimeout(timer);
          resolve();
        }
      };
      const onError = (ev: Event): void => {
        const err = mapSensorError((ev as Event & { error?: unknown }).error ?? ev);
        if (!started) {
          clearTimeout(timer);
          reject(this.fail(err));
        } else {
          this.fail(err);
          this.errors.emit(err);
        }
      };
      const timer = setTimeout(() => {
        if (!started) reject(this.fail(new OrientationError('timeout', 'Keine Sensordaten')));
      }, this.startTimeoutMs);
      sensor.addEventListener('reading', onReading);
      sensor.addEventListener('error', onError);
      this.cleanup = () => {
        clearTimeout(timer);
        sensor.removeEventListener('reading', onReading);
        sensor.removeEventListener('error', onError);
        try {
          sensor.stop();
        } catch {
          /* ignore */
        }
        if (this.sensor === sensor) this.sensor = null;
      };
      try {
        sensor.start();
      } catch (e) {
        clearTimeout(timer);
        reject(this.fail(mapSensorError(e)));
      }
    });
  }

  /**
   * Reject early only if one of the required sensor permissions is explicitly 'denied'.
   * 'granted', 'prompt' (no user gesture needed for the Generic Sensor permissions in
   * Chrome) and query errors (Chrome does not know all of these permission names) must
   * all continue to the actual sensor start, which itself surfaces a real denial.
   */
  private async checkPermissions(): Promise<void> {
    const perms = this.permissions;
    if (!perms || typeof perms.query !== 'function') return;
    for (const name of SENSOR_PERMISSIONS) {
      let state: PermissionState | null = null;
      try {
        state = (await perms.query({ name } as unknown as PermissionDescriptor)).state;
      } catch {
        state = null; // name not supported by this browser: ignore
      }
      if (state === 'denied') {
        throw this.fail(new OrientationError('denied', `Berechtigung "${name}" verweigert`));
      }
    }
  }
}

// ─── deviceorientationabsolute ───────────────────────────────────────────────

export interface DeviceOrientationOptions {
  /** Event target (default window). */
  target?: EventTarget;
  startTimeoutMs?: number;
  now?: () => number;
}

/**
 * Fallback: 'deviceorientationabsolute' events (W3C Z-X'-Y'' Euler angles,
 * same Android sensor). start() resolves on the first complete event.
 */
export class DeviceOrientationAbsoluteProvider extends BaseOrientationProvider {
  private readonly target: EventTarget | undefined;
  private readonly injected: boolean;
  private readonly startTimeoutMs: number;
  private readonly now: () => number;
  private readonly q: Quat = [0, 0, 0, 1];
  private failed = false;
  private startPromise: Promise<void> | null = null;
  private cleanup: (() => void) | null = null;

  constructor(opts: DeviceOrientationOptions = {}) {
    super('deviceorientation');
    this.injected = opts.target !== undefined;
    this.target = opts.target ?? (typeof window !== 'undefined' ? window : undefined);
    this.startTimeoutMs = opts.startTimeoutMs ?? 2000;
    this.now = opts.now ?? (() => performance.now());
  }

  get available(): boolean {
    if (this.failed || !this.target) return false;
    return this.injected || 'ondeviceorientationabsolute' in this.target;
  }

  start(): Promise<void> {
    if (!this.startPromise) this.startPromise = this.doStart();
    return this.startPromise;
  }

  stop(): void {
    this.cleanup?.();
    this.cleanup = null;
    this.startPromise = null;
  }

  private async doStart(): Promise<void> {
    const target = this.target;
    if (!target || !this.available) {
      this.failed = true;
      throw new OrientationError('unsupported', 'deviceorientationabsolute nicht verfügbar');
    }
    // iOS-style permission API (not on Chrome Android, harmless elsewhere).
    const DOE = (globalThis as { DeviceOrientationEvent?: { requestPermission?: () => Promise<string> } })
      .DeviceOrientationEvent;
    if (DOE && typeof DOE.requestPermission === 'function') {
      try {
        if ((await DOE.requestPermission()) === 'denied') {
          this.failed = true;
          throw new OrientationError('denied', 'Sensorzugriff verweigert');
        }
      } catch (e) {
        if (e instanceof OrientationError) throw e;
      }
    }

    await new Promise<void>((resolve, reject) => {
      let started = false;
      const onEvent = (e: Event): void => {
        const ev = e as Event & { alpha?: number | null; beta?: number | null; gamma?: number | null };
        const a = ev.alpha, b = ev.beta, g = ev.gamma;
        if (typeof a !== 'number' || typeof b !== 'number' || typeof g !== 'number') return;
        if (!Number.isFinite(a + b + g)) return;
        quatFromDeviceOrientation(a, b, g, this.q);
        // B.1: always stamp at reception (this.now()), not event.timeStamp.
        this.publish(this.q[0], this.q[1], this.q[2], this.q[3], this.now());
        if (!started) {
          started = true;
          clearTimeout(timer);
          resolve();
        }
      };
      const timer = setTimeout(() => {
        if (!started) {
          this.failed = true;
          this.stop();
          reject(new OrientationError('timeout', 'Keine Kompassdaten'));
        }
      }, this.startTimeoutMs);
      target.addEventListener('deviceorientationabsolute', onEvent);
      this.cleanup = () => {
        clearTimeout(timer);
        target.removeEventListener('deviceorientationabsolute', onEvent);
      };
    });
  }
}

/**
 * Last-resort fallback: plain 'deviceorientation' events, accepted only when the browser
 * marks them `absolute === true` (i.e. an earth-referenced/magnetic heading, same as
 * 'deviceorientationabsolute' would give). Some Android/Chrome combinations only fire the
 * plain event even for an absolute orientation.
 */
export class DeviceOrientationProvider extends BaseOrientationProvider {
  private readonly target: EventTarget | undefined;
  private readonly injected: boolean;
  private readonly startTimeoutMs: number;
  private readonly now: () => number;
  private readonly q: Quat = [0, 0, 0, 1];
  private failed = false;
  private startPromise: Promise<void> | null = null;
  private cleanup: (() => void) | null = null;

  constructor(opts: DeviceOrientationOptions = {}) {
    super('deviceorientation');
    this.injected = opts.target !== undefined;
    this.target = opts.target ?? (typeof window !== 'undefined' ? window : undefined);
    this.startTimeoutMs = opts.startTimeoutMs ?? 2000;
    this.now = opts.now ?? (() => performance.now());
  }

  get available(): boolean {
    if (this.failed || !this.target) return false;
    return this.injected || 'ondeviceorientation' in this.target;
  }

  start(): Promise<void> {
    if (!this.startPromise) this.startPromise = this.doStart();
    return this.startPromise;
  }

  stop(): void {
    this.cleanup?.();
    this.cleanup = null;
    this.startPromise = null;
  }

  private async doStart(): Promise<void> {
    const target = this.target;
    if (!target || !this.available) {
      this.failed = true;
      throw new OrientationError('unsupported', 'deviceorientation nicht verfügbar');
    }

    await new Promise<void>((resolve, reject) => {
      let started = false;
      const onEvent = (e: Event): void => {
        const ev = e as Event & { alpha?: number | null; beta?: number | null; gamma?: number | null; absolute?: boolean };
        if (ev.absolute !== true) return; // not earth-referenced: unusable as a compass
        const a = ev.alpha, b = ev.beta, g = ev.gamma;
        if (typeof a !== 'number' || typeof b !== 'number' || typeof g !== 'number') return;
        if (!Number.isFinite(a + b + g)) return;
        quatFromDeviceOrientation(a, b, g, this.q);
        this.publish(this.q[0], this.q[1], this.q[2], this.q[3], this.now());
        if (!started) {
          started = true;
          clearTimeout(timer);
          resolve();
        }
      };
      const timer = setTimeout(() => {
        if (!started) {
          this.failed = true;
          this.stop();
          reject(new OrientationError('timeout', 'Keine Kompassdaten'));
        }
      }, this.startTimeoutMs);
      target.addEventListener('deviceorientation', onEvent);
      this.cleanup = () => {
        clearTimeout(timer);
        target.removeEventListener('deviceorientation', onEvent);
      };
    });
  }
}

// ─── iOS (deviceorientation + webkitCompassHeading) ──────────────────────────

export type IosMotionPermission = 'granted' | 'denied' | 'unsupported';

/**
 * `DeviceOrientationEvent.requestPermission()` (iOS 13+). MUST be called synchronously
 * (no `await` before it) from inside the triggering click/tap, or Safari silently denies
 * it. Not present on Android/desktop: resolves 'unsupported' there without prompting.
 */
export function requestIosMotionPermission(): Promise<IosMotionPermission> {
  const DOE = (globalThis as { DeviceOrientationEvent?: { requestPermission?: () => Promise<string> } })
    .DeviceOrientationEvent;
  if (!DOE || typeof DOE.requestPermission !== 'function') return Promise.resolve('unsupported');
  try {
    return DOE.requestPermission().then(
      (s) => (s === 'granted' ? 'granted' : 'denied'),
      () => 'denied',
    );
  } catch {
    return Promise.resolve('denied');
  }
}

export interface IosOrientationOptions {
  /** Event target (default window). */
  target?: EventTarget;
  /** If requestIosMotionPermission() was already awaited by the caller, pass the result
   * here so start() does not call it a second time (it would no longer have the user
   * activation anyway). */
  permission?: IosMotionPermission;
  now?: () => number;
  /** Low-pass options for the compass-heading offset (default: tau 1.5 s). */
  offsetFilter?: Partial<CircularLowPassOptions>;
}

/**
 * iOS: plain `deviceorientation` events (`absolute === false`, no
 * `deviceorientationabsolute` support on any WebKit browser). The relative device
 * rotation (gyro, drift-free short-term) comes from `quatFromDeviceOrientation`;
 * it is corrected to a true-ish magnetic heading by a slowly adapting offset:
 *
 *   offset = lowpass(webkitCompassHeading - headingOf(relativeQuat))
 *
 * updated only while `webkitCompassAccuracy` is trustworthy (0..50) and the device
 * isn't pointing near straight up/down (|pitch| < 70 deg, where the compass heading
 * is unreliable). The low-pass starts fast (first ~2 s) so the offset snaps in
 * quickly, then settles to a ~1.5 s time constant so small compass jitter doesn't
 * make the view judder.
 */
export class IosOrientationProvider extends BaseOrientationProvider {
  private readonly target: EventTarget | undefined;
  private readonly injected: boolean;
  private readonly now: () => number;
  private readonly permissionOverride: IosMotionPermission | undefined;
  private readonly q: Quat = [0, 0, 0, 1];
  private readonly yawQuat: Quat = [0, 0, 0, 1];
  private readonly corrected: Quat = [0, 0, 0, 1];
  private readonly relHp: HeadingPitch = { headingDeg: 0, pitchDeg: 0 };
  private readonly offset: CircularLowPass;
  private lastSampleTime = NaN;
  private failed = false;
  private startPromise: Promise<void> | null = null;
  private cleanup: (() => void) | null = null;

  constructor(opts: IosOrientationOptions = {}) {
    super('ios-compass');
    this.injected = opts.target !== undefined;
    this.target = opts.target ?? (typeof window !== 'undefined' ? window : undefined);
    this.now = opts.now ?? (() => performance.now());
    this.permissionOverride = opts.permission;
    this.offset = new CircularLowPass({ tauS: 1.5, ...opts.offsetFilter });
  }

  get available(): boolean {
    if (this.failed || !this.target) return false;
    return this.injected || 'ondeviceorientation' in this.target;
  }

  /** Current filtered heading offset (compass - relative), deg in [-180, 180]. For tests. */
  get offsetDeg(): number {
    return this.offset.initialized ? wrap180(this.offset.value) : 0;
  }

  start(): Promise<void> {
    if (!this.startPromise) this.startPromise = this.doStart();
    return this.startPromise;
  }

  stop(): void {
    this.cleanup?.();
    this.cleanup = null;
    this.startPromise = null;
  }

  private async doStart(): Promise<void> {
    const target = this.target;
    if (!target || !this.available) {
      this.failed = true;
      throw new OrientationError('unsupported', 'deviceorientation nicht verfügbar');
    }
    const perm = this.permissionOverride ?? (await requestIosMotionPermission());
    if (perm === 'denied') {
      this.failed = true;
      throw new OrientationError('denied', 'Bewegungssensor-Zugriff verweigert');
    }

    await new Promise<void>((resolve, reject) => {
      let started = false;
      const onEvent = (e: Event): void => {
        const ev = e as Event & {
          alpha?: number | null;
          beta?: number | null;
          gamma?: number | null;
          webkitCompassHeading?: number;
          webkitCompassAccuracy?: number;
        };
        const a = ev.alpha, b = ev.beta, g = ev.gamma;
        if (typeof a !== 'number' || typeof b !== 'number' || typeof g !== 'number') return;
        if (!Number.isFinite(a + b + g)) return;
        quatFromDeviceOrientation(a, b, g, this.q);
        earthHeadingPitch(this.q, 0, this.relHp);

        const t = this.now();
        const dtS = Number.isFinite(this.lastSampleTime) ? Math.max(0, (t - this.lastSampleTime) / 1000) : 0;
        this.lastSampleTime = t;

        const ch = ev.webkitCompassHeading;
        const acc = ev.webkitCompassAccuracy;
        const accOk = typeof acc === 'number' && Number.isFinite(acc) && acc >= 0 && acc <= 50;
        const pitchOk = Math.abs(this.relHp.pitchDeg) < 70;
        if (typeof ch === 'number' && Number.isFinite(ch) && accOk && pitchOk) {
          const raw = wrap360(ch - this.relHp.headingDeg);
          this.offset.update(raw, dtS);
        }

        const offsetDeg = this.offsetDeg;
        quatFromAxisAngle(0, 0, 1, -offsetDeg * DEG2RAD, this.yawQuat);
        quatMul(this.yawQuat, this.q, this.corrected);
        this.publish(this.corrected[0], this.corrected[1], this.corrected[2], this.corrected[3], t);

        if (!started) {
          started = true;
          clearTimeout(timer);
          resolve();
        }
      };
      const timer = setTimeout(() => {
        if (!started) {
          this.failed = true;
          this.stop();
          reject(new OrientationError('timeout', 'Keine Kompassdaten'));
        }
      }, 2000);
      target.addEventListener('deviceorientation', onEvent);
      this.cleanup = () => {
        clearTimeout(timer);
        target.removeEventListener('deviceorientation', onEvent);
      };
    });
  }
}

// ─── Mock ────────────────────────────────────────────────────────────────────

export interface MockOrientationOptions {
  store?: Store<DebugSettings>;
  /**
   * Declination the engine will add (deg). The mock emits magnetic heading =
   * headingDeg - declination so that the resulting TRUE heading equals
   * headingDeg. Default 0 (headingDeg is then magnetic).
   */
  getDeclinationDeg?: () => number;
  /** Sample rate while started (Hz). */
  rateHz?: number;
  now?: () => number;
}

/**
 * Synthetic orientation for sim mode / desktop: heading from the debug store
 * (mockHeadingDeg, followed when it changes) or the setters (drag).
 */
export class MockOrientationProvider extends BaseOrientationProvider {
  readonly available = true;
  headingDeg: number;
  pitchDeg = 0;
  rollDeg = 0;
  private readonly store: Store<DebugSettings>;
  private readonly getDeclinationDeg: () => number;
  private readonly rateHz: number;
  private readonly now: () => number;
  private readonly q: Quat = [0, 0, 0, 1];
  private timer: ReturnType<typeof setInterval> | null = null;
  private unsub: (() => void) | null = null;
  private storeHeading: number;

  constructor(opts: MockOrientationOptions = {}) {
    super('mock');
    this.store = opts.store ?? debugStore;
    this.getDeclinationDeg = opts.getDeclinationDeg ?? (() => 0);
    this.rateHz = opts.rateHz ?? 30;
    this.now = opts.now ?? (() => performance.now());
    this.storeHeading = this.store.get().mockHeadingDeg;
    this.headingDeg = wrap360(this.storeHeading);
  }

  start(): Promise<void> {
    if (this.timer === null) {
      this.unsub = this.store.subscribe((v) => {
        if (v.mockHeadingDeg !== this.storeHeading) {
          this.storeHeading = v.mockHeadingDeg;
          this.headingDeg = wrap360(v.mockHeadingDeg);
          this.emitNow();
        }
      });
      this.emitNow();
      this.timer = setInterval(() => this.emitNow(), 1000 / this.rateHz);
    }
    return Promise.resolve();
  }

  stop(): void {
    if (this.timer !== null) clearInterval(this.timer);
    this.timer = null;
    this.unsub?.();
    this.unsub = null;
  }

  setHeadingDeg(deg: number): void {
    this.headingDeg = wrap360(deg);
    this.emitNow();
  }

  /** Relative change, e.g. from a horizontal drag. */
  addHeadingDeg(delta: number): void {
    this.setHeadingDeg(this.headingDeg + delta);
  }

  setPitchDeg(deg: number): void {
    this.pitchDeg = Math.max(-89, Math.min(89, deg));
    this.emitNow();
  }

  setRollDeg(deg: number): void {
    this.rollDeg = deg;
    this.emitNow();
  }

  /** Publish a sample for the current state (time = now()). */
  emitNow(time: number = this.now()): OrientationSample {
    quatFromHeadingPitchRoll(this.headingDeg - this.getDeclinationDeg(), this.pitchDeg, this.rollDeg, this.q);
    this.publish(this.q[0], this.q[1], this.q[2], this.q[3], time);
    return this.sample;
  }
}

// ─── Selection ───────────────────────────────────────────────────────────────

/**
 * Tries the candidates in order (AbsoluteOrientationSensor, then
 * deviceorientationabsolute); falls back to the next one if the active
 * provider fails at runtime.
 */
export class AutoOrientationProvider implements OrientationProvider {
  private readonly candidates: BaseOrientationProvider[];
  private active: BaseOrientationProvider | null = null;
  private pending: BaseOrientationProvider | null = null;
  private activeIndex = -1;
  private offs: (() => void)[] = [];
  private readonly samples = new Listeners<OrientationSample>();
  private readonly errors = new Listeners<OrientationError>();
  private stopped = true;

  constructor(candidates: BaseOrientationProvider[]) {
    this.candidates = candidates;
  }

  get available(): boolean {
    return this.candidates.some((c) => c.available);
  }

  /** Source of the active provider, null if none. */
  get activeSource(): OrientationSample['source'] | null {
    return this.active ? sourceOf(this.active) : null;
  }

  async start(): Promise<void> {
    this.stopped = false;
    if (this.active) return;
    await this.startFrom(0);
  }

  stop(): void {
    this.stopped = true;
    this.detach();
  }

  latest(): OrientationSample | null {
    return this.active ? this.active.latest() : null;
  }

  onSample(cb: (s: OrientationSample) => void): () => void {
    return this.samples.add(cb);
  }

  /** Emitted when all providers failed at runtime. */
  onError(cb: (e: OrientationError) => void): () => void {
    return this.errors.add(cb);
  }

  private detach(): void {
    this.offs.forEach((off) => off());
    this.offs = [];
    this.active?.stop();
    this.active = null;
    this.activeIndex = -1;
  }

  private async startFrom(index: number): Promise<void> {
    let lastErr: OrientationError = new OrientationError('unsupported', 'Kein Kompass verfügbar');
    for (let i = index; i < this.candidates.length; i++) {
      const c = this.candidates[i];
      if (!c.available) continue;
      // Subscribe before start() so the first sample (which resolves start) is forwarded too.
      this.pending = c;
      const offSample = c.onSample((s) => {
        if (this.active === c || this.pending === c) this.samples.emit(s);
      });
      try {
        await c.start();
      } catch (e) {
        offSample();
        if (this.pending === c) this.pending = null;
        lastErr = mapSensorError(e);
        continue;
      }
      if (this.pending === c) this.pending = null;
      if (this.stopped) {
        offSample();
        c.stop();
        return;
      }
      this.active = c;
      this.activeIndex = i;
      this.offs.push(offSample);
      this.offs.push(
        c.onError((err) => {
          const next = this.activeIndex + 1;
          this.detach();
          this.startFrom(next).catch((e) => this.errors.emit(mapSensorError(e ?? err)));
        }),
      );
      return;
    }
    throw lastErr;
  }
}

function sourceOf(p: BaseOrientationProvider): OrientationSample['source'] {
  return p instanceof AbsoluteOrientationProvider
    ? 'absolute-sensor'
    : p instanceof MockOrientationProvider
      ? 'mock'
      : 'deviceorientation';
}

export interface CreateOrientationOptions {
  /** Use the mock provider (default: debug.mockHeading). */
  mock?: boolean;
  store?: Store<DebugSettings>;
  absolute?: AbsoluteOrientationOptions;
  deviceOrientation?: DeviceOrientationOptions;
  /** Options for the plain 'deviceorientation' (absolute===true) fallback. Defaults to `deviceOrientation`. */
  deviceOrientationPlain?: DeviceOrientationOptions;
  mockOptions?: MockOrientationOptions;
  /** Force the iOS (webkitCompassHeading) provider on/off. Default: `isIosLike()`. */
  ios?: boolean;
  iosOptions?: IosOrientationOptions;
}

/**
 * Best available orientation provider: the mock if requested; on iOS (no
 * AbsoluteOrientationSensor / deviceorientationabsolute there) the
 * IosOrientationProvider; else an AutoOrientationProvider (AbsoluteOrientationSensor ->
 * deviceorientationabsolute -> plain deviceorientation with absolute===true, the Android
 * chain, unchanged). Call start() on the result (it rejects if no compass works at all).
 */
export function createOrientationProvider(
  opts: CreateOrientationOptions = {},
): AutoOrientationProvider | MockOrientationProvider | IosOrientationProvider {
  const store = opts.store ?? debugStore;
  const mock = opts.mock ?? store.get().mockHeading;
  if (mock) return new MockOrientationProvider({ store, ...opts.mockOptions });
  const ios = opts.ios ?? (typeof navigator !== 'undefined' && isIosLike());
  if (ios) return new IosOrientationProvider(opts.iosOptions);
  return new AutoOrientationProvider([
    new AbsoluteOrientationProvider(opts.absolute),
    new DeviceOrientationAbsoluteProvider(opts.deviceOrientation),
    new DeviceOrientationProvider(opts.deviceOrientationPlain ?? opts.deviceOrientation),
  ]);
}
